import { EntityEventDto, EntityEventType, HandledResponse } from '@/api/generator';
import { injectStrict } from '@/utils/injectStrict';
import { waitForConnectionState } from '@/utils/signalR';
import {
  HubConnectionBuilder,
  HubConnection,
  HubConnectionState,
  ISubscription,
} from '@microsoft/signalr';
import { onMounted, onUnmounted, ref, InjectionKey } from 'vue';
import { createEventHook, until } from '@vueuse/core';

export const EntityEventHubKey: InjectionKey<HubConnection> = Symbol('EntityEventHubKey');

export function getEntityEventHubConnectionSignalR() {
  const connection = new HubConnectionBuilder()
    .withUrl('/entityEventHub')
    .withAutomaticReconnect([0, 2000, 10000, 30000])
    .build();
  return connection;
}

export function useEntityEventStream() {
  const connection = injectStrict(EntityEventHubKey, getEntityEventHubConnectionSignalR());

  let subscription: ISubscription<EntityEventDto> | undefined;

  const entityEventHook = createEventHook<EntityEventDto>();

  function createEntityEventHookForJob(id: string) {
    const jobEventHookForJob = createEventHook<EntityEventType>();

    entityEventHook.on((entityEvent) => {
      if (entityEvent.entityId === id && entityEvent.event) {
        jobEventHookForJob.trigger(entityEvent.event);
      }
    });

    function executeAndAwaitEvent<T extends (...args: any[]) => Promise<HandledResponse<any>>>(
      call: T,
      awaitedEvent: EntityEventType,
      timeout = 10 * 1000,
    ) {
      return async (
        ...args: Parameters<typeof call>
      ): Promise<HandledResponse<Awaited<ReturnType<T>>['result']>> => {
        const receivedEvent = ref(false);

        function updateEvent(event: EntityEventType) {
          receivedEvent.value = event === awaitedEvent;
        }

        const { off: stopListening } = jobEventHookForJob.on(updateEvent);

        const response = await call(...args);

        if (response.success) {
          await until(receivedEvent).toBe(true, { timeout });
        }

        stopListening();

        return response;
      };
    }

    return {
      on: jobEventHookForJob.on,
      executeAndAwaitEvent,
    };
  }

  async function subscribe() {
    if (subscription) {
      subscription.dispose();
    }

    await waitForConnectionState(connection, HubConnectionState.Connected);

    subscription = connection.stream<EntityEventDto>('RegisterToEvents').subscribe({
      next: (value) => {
        entityEventHook.trigger(value);
      },
      error: (value) => {
        throw new Error('Not able to retrieve event', value);
      },
      complete: () => {
        throw new Error('Stream was completed unexpectedly');
      },
    });
  }

  onMounted(async () => {
    if (connection.state === HubConnectionState.Disconnected) {
      await connection.start();
    }

    await subscribe();
  });

  onUnmounted(async () => {
    subscription?.dispose();
    subscription = undefined;
    await connection.invoke('Unregister');
  });

  return {
    onEntityEvent: entityEventHook.on,
    createEntityEventHookForJob,
  };
}
